import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { GlassCard } from "@/components/common/GlassCard";
import { StatusPill } from "@/components/common/StatusPill";
import { LoadingState, EmptyState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Award, MapPin, School, Pencil, User, ImageIcon, Film, Users, Phone, Camera, Upload, X, Mail, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { PlayerSubNav } from "@/components/common/PlayerSubNav";
import { Player360Section } from "@/components/player/Player360Section";
import { SocialEmbeds } from "@/components/player/SocialEmbeds";
import { HealthSnapshotWidget } from "@/components/player/HealthSnapshotWidget";
import { PositionArchetypeFields } from "@/components/common/PositionArchetypeFields";

function useResolvedPlayerId() {
  const { user } = useAuth();
  const params = useParams();
  if (params.playerId) return params.playerId;
  return user?.player_id;
}

function splitName(fullName) {
  if (!fullName) return ["", ""];
  const bits = fullName.trim().split(/\s+/);
  if (bits.length === 1) return [bits[0], ""];
  return [bits[0], bits.slice(1).join(" ")];
}

function assetUrl(playerId, kind, version) {
  const token = localStorage.getItem("odl_token");
  const v = version ? `&v=${encodeURIComponent(version)}` : "";
  return `${API}/players/${playerId}/asset/${kind}?auth=${token}${v}`;
}

export default function PlayerProfilePage() {
  const playerId = useResolvedPlayerId();
  const { user } = useAuth();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const load = () => {
    if (!playerId) { setLoading(false); return; }
    setLoading(true);
    api.get(`/players/${playerId}`).then((r) => setPlayer(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [playerId]);

  if (loading) return <LoadingState />;
  if (!player) return <EmptyState icon={User} title="No player linked" description="You don't have a player linked yet." />;

  const isAdmin = user?.role === "admin";
  const [firstName, lastName] = splitName(player.full_name);
  const version = player.updated_at; // force fresh asset after upload
  const hasVideo = Boolean(player.banner_video_path);
  const hasBanner = Boolean(player.banner_path);
  const hasHeadshot = Boolean(player.headshot_path);

  const m = player.measurables || {};
  const measurableEntries = [
    ["Height", m.height],
    ["Weight", m.weight],
    ["Arm", m.arm_length],
    ["Hand", m.hand_size],
    ["40", m.forty_yard],
    ["Vert", m.vertical],
    ["Broad", m.broad_jump],
    ["Bench", m.bench_press],
    ["3-Cone", m.three_cone],
    ["Shuttle", m.shuttle],
  ].filter(([, v]) => v);

  const f = player.family_info || {};
  const familyPeople = [
    { role: "Mother", name: f.mother_name, occupation: f.mother_occupation, email: f.mother_email, phone: f.mother_phone },
    { role: "Father", name: f.father_name, occupation: f.father_occupation, email: f.father_email, phone: f.father_phone },
    { role: "Guardian", name: f.guardians, occupation: f.guardian_occupation, email: f.guardian_email, phone: f.guardian_phone },
  ].filter((p) => p.name || p.occupation || p.email || p.phone);

  return (
    <div className="max-w-[1500px] mx-auto" data-testid="player-profile-page">
      <PlayerSubNav />

      {/* HERO */}
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[color:var(--canvas-2)]/60" data-testid="profile-hero">
        {/* Banner background */}
        <div className="absolute inset-0">
          {hasVideo ? (
            <video
              data-testid="profile-banner-video"
              key={version}
              autoPlay
              loop
              muted
              playsInline
              className="h-full w-full object-cover"
              src={assetUrl(playerId, "banner_video", version)}
            />
          ) : hasBanner ? (
            <img
              data-testid="profile-banner-image"
              key={version}
              src={assetUrl(playerId, "banner", version)}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full bg-[radial-gradient(120%_120%_at_0%_0%,rgba(34,211,238,0.22),transparent_55%),radial-gradient(90%_90%_at_100%_100%,rgba(59,130,246,0.18),transparent_55%)]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--canvas-2)] via-[color:var(--canvas-2)]/70 to-transparent" />
          <div className="absolute inset-0 bg-[color:var(--canvas-2)]/30" />
        </div>

        {/* Admin asset controls */}
        {isAdmin && (
          <div className="absolute top-4 right-4 z-20 flex flex-wrap gap-2" data-testid="profile-asset-controls">
            <AssetUploadButton playerId={playerId} kind="headshot" label="Headshot" icon={Camera} onDone={load} />
            <AssetUploadButton playerId={playerId} kind="banner" label="Banner" icon={ImageIcon} onDone={load} />
            <AssetUploadButton playerId={playerId} kind="banner_video" label="Video" icon={Film} onDone={load} />
          </div>
        )}

        <div className="relative z-10 min-h-[340px] sm:min-h-[420px] p-6 sm:p-10 flex flex-col justify-end">
          <div className="flex items-end gap-6 flex-wrap">
            {/* Headshot */}
            <div className="shrink-0">
              <div
                className="h-28 w-28 sm:h-36 sm:w-36 rounded-full ring-2 ring-white/20 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)] overflow-hidden bg-gradient-to-br from-cyan-400 to-blue-500 grid place-items-center"
                data-testid="profile-headshot"
              >
                {hasHeadshot ? (
                  <img
                    key={version}
                    src={assetUrl(playerId, "headshot", version)}
                    alt={player.full_name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="text-[#04111A] font-semibold text-4xl">
                    {firstName[0]}{lastName[0]}
                  </span>
                )}
              </div>
            </div>

            {/* Name block */}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-3">
                <StatusPill tone="cyan">{player.position}</StatusPill>
                {player.jersey_number && <StatusPill tone="neutral">#{player.jersey_number}</StatusPill>}
                {player.class_year && <StatusPill tone="neutral">{player.class_year}</StatusPill>}
              </div>
              <h1
                data-testid="profile-player-name"
                className="leading-[0.9] tracking-tight text-white drop-shadow-[0_2px_14px_rgba(0,0,0,0.9)] select-none"
              >
                <span className="block font-extrabold text-[clamp(2.5rem,7vw,5.5rem)]">
                  {firstName}
                </span>
                {lastName && (
                  <span className="block font-extralight text-[clamp(2.5rem,7vw,5.5rem)] text-white/90 mt-1">
                    {lastName}
                  </span>
                )}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-white/80">
                <span className="inline-flex items-center gap-1.5"><School className="h-4 w-4 text-cyan-300" /> {player.school || "—"}</span>
                <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-cyan-300" /> {player.hometown || "—"}</span>
                <span className="inline-flex items-center gap-1.5"><Award className="h-4 w-4 text-cyan-300" /> {player.projected_round || "Unprojected"} · {player.projected_pick_range || "—"}</span>
              </div>
            </div>

            {isAdmin && (
              <Button data-testid="profile-edit-button" className="cta-primary" onClick={() => setEditOpen(true)}>
                <Pencil className="h-4 w-4" /> Edit
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* CONTENT GRID */}
      <div className="grid grid-cols-12 gap-5 mt-5">
        <Player360Section playerId={playerId} />

        {/* Health Snapshot — admin write, family/player read */}
        <div className="col-span-12 lg:col-span-6" data-testid="profile-health-wrap">
          <HealthSnapshotWidget
            player={player}
            isAdmin={isAdmin}
            onUpdated={(p) => setPlayer(p)}
            testId="profile-health-snapshot"
          />
        </div>

        {/* Bio (right rail when health is shown). When no bio, health takes
            the full row width on the next row. */}
        {player.bio ? (
          <GlassCard className="col-span-12 lg:col-span-6 p-6" testId="profile-bio-card">
            <div className="text-3 text-[10px] uppercase tracking-wider mb-2">Bio</div>
            <p className="text-2 text-base leading-relaxed">{player.bio}</p>
          </GlassCard>
        ) : (
          <div className="hidden lg:block lg:col-span-6" />
        )}

        {/* Family */}
        <GlassCard className="col-span-12 lg:col-span-6 p-6" testId="profile-family-card">
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-4 w-4 text-cyan-300" />
            <h3 className="text-1 font-semibold">Family</h3>
          </div>
          {familyPeople.length === 0 && !f.siblings ? (
            <div className="text-3 text-sm italic">No family details on file.</div>
          ) : (
            <div className="space-y-3">
              {familyPeople.map((p) => (
                <div key={p.role} className="glass p-3.5" data-testid={`profile-family-${p.role.toLowerCase()}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-3 text-[10px] uppercase tracking-wider">{p.role}</span>
                    {p.name && <span className="text-1 text-sm font-semibold">{p.name}</span>}
                  </div>
                  {p.occupation && (
                    <div className="text-2 text-xs mt-1 inline-flex items-center gap-1.5">
                      <Briefcase className="h-3 w-3 text-3" /> {p.occupation}
                    </div>
                  )}
                  {(p.email || p.phone) && (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5">
                      {p.email && (
                        <a href={`mailto:${p.email}`} className="text-cyan-300/90 hover:text-cyan-200 text-xs inline-flex items-center gap-1.5 transition">
                          <Mail className="h-3 w-3" /> {p.email}
                        </a>
                      )}
                      {p.phone && (
                        <a href={`tel:${p.phone}`} className="text-cyan-300/90 hover:text-cyan-200 text-xs inline-flex items-center gap-1.5 transition">
                          <Phone className="h-3 w-3" /> {p.phone}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {f.siblings && (
                <div className="flex items-start gap-3 pt-1">
                  <div className="w-24 text-3 text-[11px] uppercase tracking-wider pt-0.5">Siblings</div>
                  <div className="text-1 text-sm font-medium flex-1">{f.siblings}</div>
                </div>
              )}
            </div>
          )}
        </GlassCard>

        {/* High School */}
        <GlassCard className="col-span-12 lg:col-span-6 p-6" testId="profile-highschool-card">
          <div className="flex items-center gap-2 mb-4">
            <School className="h-4 w-4 text-cyan-300" />
            <h3 className="text-1 font-semibold">High School</h3>
          </div>
          {f.high_school || f.hs_coach_name || f.hs_coach_contact ? (
            <div className="space-y-3">
              {f.high_school && (
                <div className="flex items-start gap-3">
                  <div className="w-24 text-3 text-[11px] uppercase tracking-wider pt-0.5">School</div>
                  <div className="text-1 text-sm font-medium flex-1">{f.high_school}</div>
                </div>
              )}
              {f.hs_coach_name && (
                <div className="flex items-start gap-3">
                  <div className="w-24 text-3 text-[11px] uppercase tracking-wider pt-0.5">HS Coach</div>
                  <div className="text-1 text-sm font-medium flex-1">{f.hs_coach_name}</div>
                </div>
              )}
              {f.hs_coach_contact && (
                <div className="flex items-start gap-3">
                  <div className="w-24 text-3 text-[11px] uppercase tracking-wider pt-0.5">Contact</div>
                  <div className="text-2 text-sm flex-1 inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-3" /> {f.hs_coach_contact}</div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-3 text-sm italic">No high school details on file.</div>
          )}
        </GlassCard>

        {/* Measurables */}
        <GlassCard className="col-span-12 p-6" testId="profile-measurables">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-1 font-semibold">Measurables</h3>
            <span className="text-3 text-xs">Combine · Pro Day</span>
          </div>
          {measurableEntries.length === 0 ? (
            <div className="text-3 text-sm italic">No measurables recorded yet.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
              {measurableEntries.map(([k, v]) => (
                <div key={k} className="glass p-3">
                  <div className="text-3 text-[10px] uppercase tracking-wider">{k}</div>
                  <div className="text-1 font-mono text-sm mt-1">{v}</div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            <div className="glass p-4">
              <div className="text-3 text-[10px] uppercase tracking-wider">Confidence</div>
              <div className="text-1 text-2xl font-semibold mt-0.5">{player.confidence || 0}%</div>
              <div className="mt-2 h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500" style={{ width: `${player.confidence || 0}%` }} />
              </div>
            </div>
            <div className="glass p-4">
              <div className="text-3 text-[10px] uppercase tracking-wider">Projection</div>
              <div className="text-1 text-2xl font-semibold mt-0.5">{player.projected_round || "—"}</div>
              <div className="text-2 text-xs mt-0.5">{player.projected_pick_range || "—"}</div>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Phase 14 — social & film embeds */}
      <div className="mt-5">
        <SocialEmbeds social={player.social_links} />
      </div>

      {editOpen && (
        <EditPlayerDialog open={editOpen} onOpenChange={setEditOpen} player={player} onSaved={(p) => { setPlayer(p); toast.success("Profile updated"); }} />
      )}
    </div>
  );
}

function AssetUploadButton({ playerId, kind, label, icon: Icon, onDone }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/players/${playerId}/upload-asset?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${label} uploaded`);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${label} upload failed`);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const accept = kind === "banner_video" ? "video/*" : "image/*";
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleChange}
        className="hidden"
        data-testid={`profile-upload-${kind}-input`}
      />
      <button
        type="button"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        data-testid={`profile-upload-${kind}-button`}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white/90 bg-black/40 backdrop-blur-md border border-white/15 hover:bg-black/60 transition disabled:opacity-50"
      >
        {busy ? <Upload className="h-3.5 w-3.5 animate-pulse" /> : <Icon className="h-3.5 w-3.5" />}
        {busy ? "Uploading…" : label}
      </button>
    </>
  );
}

function EditPlayerDialog({ open, onOpenChange, player, onSaved }) {
  const [form, setForm] = useState({
    ...player,
    measurables: { ...(player.measurables || {}) },
    family_info: { ...(player.family_info || {}) },
    social_links: { ...(player.social_links || {}) },
  });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setM = (k, v) => setForm((s) => ({ ...s, measurables: { ...s.measurables, [k]: v } }));
  const setF = (k, v) => setForm((s) => ({ ...s, family_info: { ...s.family_info, [k]: v } }));
  const setSocial = (k, v) => setForm((s) => ({ ...s, social_links: { ...s.social_links, [k]: v } }));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/players/${player.id}`, form);
      onSaved(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[color:var(--canvas-2)]/95 backdrop-blur-2xl border-white/10" data-testid="edit-player-dialog">
        <DialogHeader><DialogTitle className="text-1">Edit player</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <PositionArchetypeFields
              position={form.position}
              archetype={form.archetype}
              onPositionChange={(v) => set("position", v)}
              onArchetypeChange={(v) => set("archetype", v)}
              testIdPrefix="edit"
            />
          </div>
          {[["full_name", "Full name"], ["school", "School"], ["class_year", "Class"], ["jersey_number", "Jersey #"], ["hometown", "Hometown"], ["projected_round", "Projected round"], ["projected_pick_range", "Pick range"]].map(([k, label]) => (
            <div key={k} className="space-y-1">
              <Label className="text-2 text-xs">{label}</Label>
              <Input data-testid={`edit-${k}`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className="bg-white/5 border-white/10 text-1" />
            </div>
          ))}
          <div className="col-span-2 space-y-1">
            <Label className="text-2 text-xs">Confidence ({form.confidence || 0}%)</Label>
            <Input type="range" min="0" max="100" value={form.confidence || 0} onChange={(e) => set("confidence", parseInt(e.target.value))} />
          </div>
          <div className="col-span-2 space-y-1">
            <Label className="text-2 text-xs">Bio</Label>
            <Textarea data-testid="edit-bio" rows={2} value={form.bio || ""} onChange={(e) => set("bio", e.target.value)} className="bg-white/5 border-white/10 text-1" />
          </div>
          <div className="col-span-2 space-y-1">
            <Label className="text-2 text-xs">Player Narrative (markdown supported)</Label>
            <Textarea data-testid="edit-narrative" rows={4} value={form.narrative_md || ""} onChange={(e) => set("narrative_md", e.target.value)} className="bg-white/5 border-white/10 text-1" placeholder="Short story framing the player's draft profile…" />
          </div>
          <div className="col-span-2 space-y-1">
            <Label className="text-2 text-xs">Projection Trend</Label>
            <select
              data-testid="edit-projection-trend"
              value={form.projection_trend || "stable"}
              onChange={(e) => set("projection_trend", e.target.value)}
              className="w-full h-9 rounded-md bg-white/5 border border-white/10 text-1 px-3 text-sm"
            >
              <option value="up">Up</option>
              <option value="stable">Stable</option>
              <option value="down">Down</option>
            </select>
          </div>
          <div className="col-span-2 space-y-1">
            <Label className="text-2 text-xs">Projection AI Explanation (markdown)</Label>
            <Textarea data-testid="edit-projection-explanation" rows={3} value={form.projection_explanation_md || ""} onChange={(e) => set("projection_explanation_md", e.target.value)} className="bg-white/5 border-white/10 text-1" placeholder="Why this round/range right now…" />
          </div>
        </div>

        <div className="text-3 text-[11px] uppercase tracking-wider mt-2">Family</div>
        <div className="space-y-3">
          {[
            { role: "Mother", keys: { name: "mother_name", occ: "mother_occupation", email: "mother_email", phone: "mother_phone" } },
            { role: "Father", keys: { name: "father_name", occ: "father_occupation", email: "father_email", phone: "father_phone" } },
            { role: "Guardian", keys: { name: "guardians", occ: "guardian_occupation", email: "guardian_email", phone: "guardian_phone" } },
          ].map(({ role, keys }) => (
            <div key={role} className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
              <div className="text-2 text-[11px] font-semibold mb-2">{role}</div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-2 text-[11px]">Name</Label>
                  <Input data-testid={`edit-family-${keys.name}`} value={form.family_info?.[keys.name] || ""} onChange={(e) => setF(keys.name, e.target.value)} className="bg-white/5 border-white/10 text-1" />
                </div>
                <div className="space-y-1">
                  <Label className="text-2 text-[11px]">Occupation / Workplace</Label>
                  <Input data-testid={`edit-family-${keys.occ}`} value={form.family_info?.[keys.occ] || ""} onChange={(e) => setF(keys.occ, e.target.value)} className="bg-white/5 border-white/10 text-1" />
                </div>
                <div className="space-y-1">
                  <Label className="text-2 text-[11px]">Email</Label>
                  <Input data-testid={`edit-family-${keys.email}`} type="email" value={form.family_info?.[keys.email] || ""} onChange={(e) => setF(keys.email, e.target.value)} className="bg-white/5 border-white/10 text-1" />
                </div>
                <div className="space-y-1">
                  <Label className="text-2 text-[11px]">Phone</Label>
                  <Input data-testid={`edit-family-${keys.phone}`} value={form.family_info?.[keys.phone] || ""} onChange={(e) => setF(keys.phone, e.target.value)} className="bg-white/5 border-white/10 text-1" />
                </div>
              </div>
            </div>
          ))}
          <div className="space-y-1">
            <Label className="text-2 text-[11px]">Siblings</Label>
            <Input data-testid="edit-family-siblings" value={form.family_info?.siblings || ""} onChange={(e) => setF("siblings", e.target.value)} className="bg-white/5 border-white/10 text-1" />
          </div>
        </div>

        <div className="text-3 text-[11px] uppercase tracking-wider mt-2">High School</div>
        <div className="grid grid-cols-2 gap-2">
          {[
            ["high_school", "High school"],
            ["hs_coach_name", "HS coach name"],
            ["hs_coach_contact", "HS coach contact"],
          ].map(([k, label]) => (
            <div key={k} className="space-y-1">
              <Label className="text-2 text-[11px]">{label}</Label>
              <Input data-testid={`edit-family-${k}`} value={form.family_info?.[k] || ""} onChange={(e) => setF(k, e.target.value)} className="bg-white/5 border-white/10 text-1" />
            </div>
          ))}
        </div>

        <div className="text-3 text-[11px] uppercase tracking-wider mt-2">Measurables</div>
        <div className="grid grid-cols-3 gap-2">
          {["height", "weight", "arm_length", "hand_size", "forty_yard", "vertical", "broad_jump", "bench_press", "three_cone", "shuttle"].map((k) => (
            <div key={k} className="space-y-1">
              <Label className="text-2 text-[11px] capitalize">{k.replace(/_/g, " ")}</Label>
              <Input value={form.measurables?.[k] || ""} onChange={(e) => setM(k, e.target.value)} className="bg-white/5 border-white/10 text-1" />
            </div>
          ))}
        </div>

        <div className="text-3 text-[11px] uppercase tracking-wider mt-2">Social &amp; Film links</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            ["hudl",      "Hudl URL"],
            ["youtube",   "YouTube URL"],
            ["twitter",   "Twitter / X URL"],
            ["instagram", "Instagram URL"],
            ["tiktok",    "TikTok URL"],
            ["website",   "Website URL"],
          ].map(([k, label]) => (
            <div key={k} className="space-y-1">
              <Label className="text-2 text-[11px]">{label}</Label>
              <Input
                data-testid={`edit-social-${k}`}
                value={form.social_links?.[k] || ""}
                onChange={(e) => setSocial(k, e.target.value)}
                className="bg-white/5 border-white/10 text-1"
                placeholder={k === "hudl" ? "https://www.hudl.com/video/…" : "https://"}
              />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-2">Cancel</Button>
          <Button data-testid="edit-save" disabled={saving} onClick={save} className="cta-primary">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
